// src/Calculators/CommonComponents/Sidebar.jsx
import React, { useState, useEffect } from "react";
import { NavLink } from "react-router-dom";
import {
  X,
  ChevronDown,
  ChevronRight,
  TrendingUp,
  Home,
  CreditCard,
  Briefcase,
  FileText,
  Activity,
  BookOpen,
  Calculator,
} from "lucide-react";
import "./Sidebar.css";

const Sidebar = ({ isOpen, toggleSidebar }) => {
  const [expandedCategories, setExpandedCategories] = useState({
    investment: true,
    loans: false,
    business: false,
    taxation: false,
    daily: false,
    health: false,
    businessTools: false,
    technical: false,
  });

  //  Ensure sidebar opens automatically when the page loads
  useEffect(() => {
    if (!isOpen) {
      toggleSidebar(); // Open sidebar if it's closed initially
    }
  }, []); // runs once when Sidebar mounts

  const calculatorRoutes = {
    "SIP Calculator": "/finance/sip",
    "Mutual Fund Return Calculator": "/finance/mutual",
    "Compound Interest Calculator": "/finance/compound",
    "Simple Interest Calculator": "/finance/simple",
    "FD Calculator": "/finance/fd",
    "RD Calculator": "/finance/rd",
    "PPF Calculator": "/finance/ppf",
    "NPS Calculator": "/finance/nps",
    "Sukanya Samriddhi Yojana Calculator": "/finance/ssy",
    "Retirement Corpus Calculator": "/finance/retirement",
    "Goal-Based Investment Calculator": "/finance/goal",
    "EMI Calculator": "/finance/emi",
    "Loan Amortization Schedule": "/finance/loan",
    "Loan Eligibility Calculator": "/finance/loanEligibility",
    "Prepayment Impact Calculator": "/finance/prepayment",
    "Balance Transfer Calculator": "/finance/balance",
    "Credit Card Interest Calculator": "/finance/credit",
    "Debt-to-Income Ratio Calculator": "/finance/debt",
    "ROI Calculator": "/finance/roi",
    "Break-Even Point Calculator": "/finance/break",
    "Profit Margin Calculator": "/finance/profit",
    "NPV Calculator": "/finance/npv",
    "IRR Calculator": "/finance/irr",
    "DCF Calculator": "/finance/dcf",
    "Income Tax Calculator": "/finance/income",
    "GST Calculator": "/finance/gst",
    "HRA Exemption Calculator": "/finance/hra",
    "Gratuity Calculator": "/finance/gratuity",
    "PF Calculator": "/finance/pf",
    "TDS Calculator": "/finance/tds",
    "Capital Gains Tax Calculator": "/finance/capital",
    "Unit Converter": "/finance/unit",
    "Tip Calculator": "/finance/tip",
    "Discount Calculator": "/finance/discount",
    "Percentage Calculator":"/finance/percentage",
    "Age Calculator":"/finance/age",
    "Date Difference Calculator":"/finance/date",
    "Salary Breakup Calculator":"/finance/salary",
    "Currency Converter":"/finance/currency",
    "BMI Calculator":"/finance/bmi",
    "BMR Calculator":"/finance/bmr",
    "Calorie Intake Calculator":"/finance/calorie",
    "Ideal Weight Calculator":"/finance/ideal",
    "Heart Rate Zone Calculator":"/finance/heart",
    "Body Fat Percentage Calculator":"/finance/body",
    "Pregnancy Due Date Calculator":"/finance/pregnancy",
    "Inventory Valuation Calculator":"/finance/inventory",
    "Invoice Calculator":"/finance/invoice",
    "Depreciation Calculator":"/finance/depreciation",
    "Gross/Net Profit Calculator":"/finance/gross",
    "Loan vs Lease Calculator":"/finance/loanvslease",
    "CAGR Calculator":"/finance/cagr",
    "EBITDA Calculator":"/finance/ebitda",
    "Percentage Change Calculator":"/finance/percentageChange",
    "Time Duration Calculator":"/finance/time",
    "Fuel Cost Calculator":"/finance/fuel",
    "Electricity Bill Estimator":"/finance/electricity",
    "Savings Goal Timeline":"/finance/savings",
    "Currency Inflation Adjuster":"/finance/inflation"
  };

  const calculatorCategories = {
    investment: {
      title: "Investment & Savings",
      icon: <TrendingUp size={20} />,
      calculators: [
        "SIP Calculator",
        "Mutual Fund Return Calculator",
        "Compound Interest Calculator",
        "Simple Interest Calculator",
        "FD Calculator",
        "RD Calculator",
        "PPF Calculator",
        "NPS Calculator",
        "Sukanya Samriddhi Yojana Calculator",
        "Retirement Corpus Calculator",
        "Goal-Based Investment Calculator",
      ],
    },
    loans: {
      title: "Loans & Credit",
      icon: <CreditCard size={20} />,
      calculators: [
        "EMI Calculator",
        "Loan Amortization Schedule",
        "Loan Eligibility Calculator",
        "Prepayment Impact Calculator",
        "Balance Transfer Calculator",
        "Credit Card Interest Calculator",
        "Debt-to-Income Ratio Calculator",
      ],
    },
    business: {
      title: "Business / ROI",
      icon: <Briefcase size={20} />,
      calculators: [
        "ROI Calculator",
        "Break-Even Point Calculator",
        "Profit Margin Calculator",
        "NPV Calculator",
        "IRR Calculator",
        "DCF Calculator",
      ],
    },
    taxation: {
      title: "Taxation",
      icon: <FileText size={20} />,
      calculators: [
        "Income Tax Calculator",
        "GST Calculator",
        "HRA Exemption Calculator",
        "Gratuity Calculator",
        "PF Calculator",
        "TDS Calculator",
        "Capital Gains Tax Calculator",
      ],
    },
    daily: {
      title: "Daily Life & Personal",
      icon: <Home size={20} />,
      calculators: [
        "Unit Converter",
        "Tip Calculator",
        "Discount Calculator",
        "Percentage Calculator",
        "Age Calculator",
        "Date Difference Calculator",
        "Salary Breakup Calculator",
        "Currency Converter",
      ],
    },
    health: {
      title: "Health & Fitness",
      icon: <Activity size={20} />,
      calculators: [
        "BMI Calculator",
        "BMR Calculator",
        "Calorie Intake Calculator",
        "Ideal Weight Calculator",
        "Heart Rate Zone Calculator",
        "Body Fat Percentage Calculator",
        "Pregnancy Due Date Calculator",
      ],
    },
    businessTools: {
      title: "Business & Accounting",
      icon: <Calculator size={20} />,
      calculators: [
        "Invoice Calculator",
        "Inventory Valuation Calculator",
        "Depreciation Calculator",
        "Gross/Net Profit Calculator",
        "Loan vs Lease Calculator",
        "CAGR Calculator",
        "EBITDA Calculator",
      ],
    },
    technical: {
      title: "Technical & Utility",
      icon: <BookOpen size={20} />,
      calculators: [
        "Percentage Change Calculator",
        "Time Duration Calculator",
        "Fuel Cost Calculator",
        "Electricity Bill Estimator",
        "Savings Goal Timeline",
        "Currency Inflation Adjuster",
      ],
    },
  };

  const toggleCategory = (category) => {
    setExpandedCategories((prev) => ({
      ...prev,
      [category]: !prev[category],
    }));
  };

  return (
    <>
      {isOpen && <div className="sidebar-overlay" onClick={toggleSidebar}></div>}

      <aside className={`calc-sidebar ${isOpen ? "open" : ""}`}>
        <div className="sidebar-header">
          <h2 className="sidebar-title">ALL CALCULATORS</h2>
          <button className="close-sidebar" onClick={toggleSidebar}>
            <X size={24} />
          </button>
        </div>
        
        <div className="sidebar-content">
          {Object.entries(calculatorCategories).map(([key, category]) => (
            <div key={key} className="calculator-category">
              <button
                className="category-header"
                onClick={() => toggleCategory(key)}
              >
                <div className="category-title">
                  {category.icon}
                  <span>{category.title}</span>
                </div>
                {expandedCategories[key] ? (
                  <ChevronDown size={20} />
                ) : (
                  <ChevronRight size={20} />
                )}
              </button>

              {expandedCategories[key] && (
                <ul className="calculator-list">
                  {category.calculators.map((calc, idx) => (
                    <li key={idx}>
                      <NavLink
                        to={calculatorRoutes[calc] || "#"}
                        className={({ isActive }) =>
                          `calculator-link ${isActive ? "active" : ""}`
                        }
                        onClick={toggleSidebar}
                      >
                        {calc}
                      </NavLink>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          ))}
        </div>
      </aside>
    </>
  );
};

export default Sidebar;
